/**
 * `pending-localization-smoke` v0.1 — locale fan-out matrix
 * orchestration contracts.
 *
 * One flow + one locale list → N parallel runs, one per locale.
 * After every cell finishes the runner emits per-locale findings
 * (overflow / RTL overlap / text-truncation), the aggregator
 * collects them, and the matrix UI renders one column per locale
 * with a passed / failed / unknown badge.
 *
 * Vendor-agnostic by construction: the matrix only varies the
 * locale dimension; the existing capability resolver still picks
 * the browser. v0.1 ships a single shared capability (defaults
 * to Chromium / Linux); a v1+ ship can promote `capability` to
 * per-cell.
 *
 * Design choices:
 *   • The anchor is the source-of-truth locale (typically `en-US`)
 *     — operators expect this run to be untouched by
 *     pseudo-translation. The aggregator gives it `verdict:
 *     'anchor'` and never marks it `failed` on layout findings.
 *   • `cells` carries `LocalizationMatrixCell { locale,
 *     pseudoModeOverride? }`. The matrix-wide `pseudoMode` is the
 *     default; per-cell override lets an operator pin one locale
 *     to a different mode (e.g. keep `en-US` at `bracketed` for
 *     bracket-leak detection while running `ja-JP` at `extended`).
 *   • `expandLocalizationMatrix(req)` is a pure helper the
 *     orchestrator uses to flatten cells into the parallel-runs
 *     `capabilities` array.
 *   • `aggregateLocalizationMatrixResults(...)` takes a per-cell
 *     finding count and emits the verdict + summary. Pure
 *     function — no fetch, no DB, easy to test.
 */

import { z } from 'zod';

// ============================================================================
// Locked vocabulary
// ============================================================================

/** Locked-decision §2 of the v0 design doc — three pseudo modes. */
export const PSEUDO_LOCALE_MODES = ['bracketed', 'extended', 'both'] as const;
export const PseudoLocaleModeSchema = z.enum(PSEUDO_LOCALE_MODES);
export type PseudoLocaleMode = z.infer<typeof PseudoLocaleModeSchema>;

/**
 * BCP-47-ish locale tag. We don't enforce the full IANA shape here
 * — the API service validates against `LOCALE_REGISTRY` (the v0
 * `pseudo-translate.ts` lib) for the canonical 13-locale list.
 * This regex only catches obvious garbage.
 */
export const LocaleCodeSchema = z
  .string()
  .min(2)
  .max(16)
  .regex(/^[a-z]{2,3}(-[A-Z]{2})?$/, 'invalid locale tag (expected `xx-YY` or `xx`)');

// ============================================================================
// Cell + request shapes
// ============================================================================

export const LocalizationMatrixCellSchema = z
  .object({
    locale: LocaleCodeSchema,
    /**
     * Optional per-cell mode override. When unset the matrix-wide
     * `pseudoMode` wins. v0.1 ships this as forward-compat so the
     * v1 per-cell mode UI doesn't need a contract bump.
     */
    pseudoModeOverride: PseudoLocaleModeSchema.optional(),
  })
  .strict();
export type LocalizationMatrixCell = z.infer<typeof LocalizationMatrixCellSchema>;

/**
 * Submit-time request body for `POST /api/localization-matrix/run`.
 *
 * `anchorLocale` resolves to whichever cell in `cells` matches.
 * When unset OR no match, the first cell wins (the
 * `pickLocalizationAnchor()` helper enforces this).
 *
 * `cells` is capped at 24 (locked-decision §4 mirrors visual-
 * matrix cap) and floored at 2 (one locale isn't a matrix —
 * that's just a regular run). The per-org
 * `localization.maxLocales` may impose a tighter cap at
 * run-submit time.
 */
export const StartLocalizationMatrixRunRequestSchema = z.object({
  flowId: z.string().min(1),
  flowName: z.string().min(1).optional(),
  projectId: z.string().uuid().optional(),
  environment: z.string().min(1).optional(),
  cells: z.array(LocalizationMatrixCellSchema).min(2).max(24),
  /**
   * Matrix-wide pseudo-translation mode. Each cell's
   * `pseudoModeOverride` wins when set; otherwise this is the
   * effective mode for that cell. v0.1 default `'both'`
   * (locked-decision §2).
   */
  pseudoMode: PseudoLocaleModeSchema.default('both'),
  /**
   * Optional anchor pin. When set, this exact locale is the
   * anchor (errors with `LOCALIZATION_MATRIX_ANCHOR_NOT_FOUND` if
   * no cell matches). When unset, `cells[0]` is the anchor.
   *
   * The anchor is the "source-of-truth" locale (usually `en-US`)
   * which the runner does NOT pseudo-translate. All other cells
   * are pseudo-translated and compared (visually + via the axe
   * overflow rule) against the anchor.
   */
  anchorLocale: LocaleCodeSchema.optional(),
});
export type StartLocalizationMatrixRunRequest = z.infer<typeof StartLocalizationMatrixRunRequestSchema>;

// ============================================================================
// Result shapes
// ============================================================================

/** Verdict for one cell in the aggregated result. */
export const LocalizationMatrixCellVerdictSchema = z.enum(['passed', 'failed', 'anchor', 'unknown']);
export type LocalizationMatrixCellVerdict = z.infer<typeof LocalizationMatrixCellVerdictSchema>;

/**
 * Per-finding shape the runner emits. v0.1 ships 3 finding kinds:
 *
 *   - `overflow_truncation` — pseudo-translated text was clipped
 *     by `overflow: hidden` / `text-overflow: ellipsis`.
 *   - `rtl_overlap` — in an RTL locale (Arabic / Hebrew), two
 *     elements that should be adjacent now overlap because the
 *     mirror-layout heuristics failed.
 *   - `text_clipped_by_container` — element's `scrollWidth`
 *     exceeds its `clientWidth` after pseudo-translation.
 *
 * v0.2 may add `currency_format_mismatch`,
 * `date_format_mismatch`, `untranslated_string`.
 */
export const LocalizationFindingKindSchema = z.enum([
  'overflow_truncation',
  'rtl_overlap',
  'text_clipped_by_container',
]);
export type LocalizationFindingKind = z.infer<typeof LocalizationFindingKindSchema>;

export const LocalizationFindingSchema = z
  .object({
    kind: LocalizationFindingKindSchema,
    /** CSS selector (best-effort) of the offending element. */
    selector: z.string().min(1).max(512),
    /** Snippet of the pseudo-translated text that triggered the
     *  finding. Truncated to 200 chars. */
    sample: z.string().max(200).optional(),
    /** Optional severity score 0..1 — the axe rule reports this
     *  for ordering. */
    severity: z.number().min(0).max(1).optional(),
  })
  .strict();
export type LocalizationFinding = z.infer<typeof LocalizationFindingSchema>;

export const LocalizationMatrixCellResultSchema = z
  .object({
    locale: LocaleCodeSchema,
    effectivePseudoMode: PseudoLocaleModeSchema,
    verdict: LocalizationMatrixCellVerdictSchema,
    /** Findings the runner emitted for this cell. Empty for the
     *  anchor (we never pseudo-translate the anchor) and for the
     *  unknown rows (runner didn't capture). */
    findings: z.array(LocalizationFindingSchema),
    /** Short human-readable note explaining the verdict — used
     *  in tooltips on the grid. Examples: "0 layout findings",
     *  "3 overflow + 1 RTL overlap", "Run cancelled". */
    notes: z.string().nullable(),
  })
  .strict();
export type LocalizationMatrixCellResult = z.infer<typeof LocalizationMatrixCellResultSchema>;

export const LocalizationMatrixRunResultSchema = z
  .object({
    parentRunId: z.string().uuid(),
    flowId: z.string(),
    anchorLocale: LocaleCodeSchema,
    pseudoMode: PseudoLocaleModeSchema,
    cells: z.array(LocalizationMatrixCellResultSchema),
    summary: z.object({
      total: z.number().int().min(0),
      passed: z.number().int().min(0),
      failed: z.number().int().min(0),
      unknown: z.number().int().min(0),
      totalFindings: z.number().int().min(0),
      worstLocale: LocaleCodeSchema.nullable(),
    }),
  })
  .strict();
export type LocalizationMatrixRunResult = z.infer<typeof LocalizationMatrixRunResultSchema>;

// ============================================================================
// Artifact shape the aggregator consumes
// ============================================================================

/**
 * Opaque artifact shape `aggregateLocalizationMatrixResults`
 * consumes — the orchestrator collects one per child run. The
 * findings come from the runner's post-run axe-rule extension
 * (v0.1 ships as `lib/localization/overflow-axe-rule.ts`).
 *
 * `unavailable: true` is set when the child run was cancelled,
 * crashed, or hasn't completed yet — the aggregator then emits
 * `verdict: 'unknown'` for that cell.
 */
export interface LocalizationMatrixCellArtifact {
  locale: string;
  unavailable: boolean;
  /** Optional capture-side error string the runner emits for
   *  the UI tooltip. */
  captureError?: string | null;
  /** Findings the runner's axe rule produced. Empty array is
   *  legal — that means "ran cleanly, no layout issues". */
  findings: ReadonlyArray<LocalizationFinding>;
}

// ============================================================================
// Pure helpers
// ============================================================================

/**
 * Resolve the anchor cell given a locale hint and the cell list.
 * Returns `null` when `anchorHint` is set but no cell matches
 * (the caller raises `LOCALIZATION_MATRIX_ANCHOR_NOT_FOUND`).
 * Returns the first cell when `anchorHint` is unset.
 */
export function pickLocalizationAnchor(
  cells: ReadonlyArray<{ locale: string }>,
  anchorHint?: string | null,
): { index: number; locale: string } | null {
  if (cells.length === 0) return null;
  if (anchorHint) {
    const idx = cells.findIndex((c) => c.locale === anchorHint);
    if (idx === -1) return null;
    return { index: idx, locale: cells[idx].locale };
  }
  return { index: 0, locale: cells[0].locale };
}

/**
 * Resolve the effective pseudoMode for a cell: per-cell override
 * wins when set, otherwise the matrix-wide default. Used by the
 * service when populating per-child-run metadata.
 */
export function resolveEffectivePseudoMode(
  cell: LocalizationMatrixCell,
  matrixMode: PseudoLocaleMode,
): PseudoLocaleMode {
  return cell.pseudoModeOverride ?? matrixMode;
}

/**
 * Pure helper — given the matrix request, the anchor locale, and
 * an artifact per cell, produce one
 * `LocalizationMatrixCellResult` per cell + the summary.
 *
 * Verdict rules:
 *   • The anchor cell always gets `verdict: 'anchor'`, empty
 *     findings (we don't pseudo-translate the anchor).
 *   • If the cell's artifact is unavailable, `verdict: 'unknown'`.
 *   • Otherwise: empty findings → `'passed'`; any findings →
 *     `'failed'`.
 *
 * `summary.worstLocale` is the locale with the most findings
 * (ties broken by submit order); `null` when every cell passed.
 */
export function aggregateLocalizationMatrixResults(
  req: StartLocalizationMatrixRunRequest,
  anchorLocale: string,
  artifacts: ReadonlyArray<LocalizationMatrixCellArtifact>,
  parentRunId: string,
): LocalizationMatrixRunResult {
  const artifactByLocale = new Map(artifacts.map((a) => [a.locale, a]));

  const cellResults: LocalizationMatrixCellResult[] = req.cells.map((cell) => {
    const effectivePseudoMode = resolveEffectivePseudoMode(cell, req.pseudoMode);

    if (cell.locale === anchorLocale) {
      return {
        locale: cell.locale,
        effectivePseudoMode,
        verdict: 'anchor',
        findings: [],
        notes: 'Anchor locale — source-of-truth reference (no pseudo-translation applied)',
      };
    }

    const art = artifactByLocale.get(cell.locale);
    if (!art || art.unavailable) {
      return {
        locale: cell.locale,
        effectivePseudoMode,
        verdict: 'unknown',
        findings: [],
        notes: art?.captureError ?? 'Run did not complete — findings unavailable.',
      };
    }

    const findings = Array.from(art.findings);
    const passed = findings.length === 0;
    return {
      locale: cell.locale,
      effectivePseudoMode,
      verdict: passed ? 'passed' : 'failed',
      findings,
      notes: passed ? '0 layout findings' : describeFindings(findings),
    };
  });

  let worstLocale: string | null = null;
  let worstCount = 0;
  for (const r of cellResults) {
    if (r.verdict !== 'failed') continue;
    if (r.findings.length > worstCount) {
      worstCount = r.findings.length;
      worstLocale = r.locale;
    }
  }

  const totalFindings = cellResults.reduce((acc, c) => acc + c.findings.length, 0);

  return {
    parentRunId,
    flowId: req.flowId,
    anchorLocale,
    pseudoMode: req.pseudoMode,
    cells: cellResults,
    summary: {
      total: cellResults.length,
      passed: cellResults.filter((c) => c.verdict === 'passed').length,
      failed: cellResults.filter((c) => c.verdict === 'failed').length,
      unknown: cellResults.filter((c) => c.verdict === 'unknown').length,
      totalFindings,
      worstLocale,
    },
  };
}

function describeFindings(findings: ReadonlyArray<LocalizationFinding>): string {
  const counts = new Map<LocalizationFindingKind, number>();
  for (const f of findings) {
    counts.set(f.kind, (counts.get(f.kind) ?? 0) + 1);
  }
  const parts: string[] = [];
  for (const [kind, n] of counts) {
    parts.push(`${n} ${kind.replace(/_/g, ' ')}`);
  }
  return parts.join(', ');
}
